// Validation Service - Handles profile validation API calls

import { apiGet, apiPost, apiDelete, API_BASE } from '@/utils/api';

export interface SubmitProfileRequest {
  /** Required on first submission, optional on resubmission (signature is preserved) */
  reglementInterieurSigned?: boolean;
  reglementInterieurVersionId?: string;
  consentementMediasAccepted?: boolean;
  consentementMediasVersionId?: string;
}

export interface SignatureStatus {
  reglementInterieurSigned: boolean;
  reglementInterieurSignedAt: string | null;
  reglementInterieurVersionId: string | null;
  consentementMediasSigned: boolean;
  consentementMediasSignedAt: string | null;
  consentementMediasAccepted: boolean;
  consentementMediasVersionId: string | null;
}

export interface ValidationStatus {
  status: 'DRAFT' | 'PENDING_VALIDATION' | 'APPROVED' | 'REJECTED';
  requiresValidation: boolean;
  isResubmission?: boolean;
  signatureStatus?: SignatureStatus;
  validationRequest?: {
    id: string;
    submittedAt: string;
    status: string;
    reglementInterieurSignedAt?: string;
    consentementMediasAccepted: boolean;
    rejectionReason?: string;
    rejectionFields?: string[];
    validatedBy?: string;
    validatedAt?: string;
  };
}

export interface PendingValidation {
  id: string;
  userId: string;
  tenantId: string;
  status: string;
  submittedAt: string;
  user: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    phone?: string;
    role: string;
    isAlsoProfessor?: boolean;
    userCampuses: Array<{
      campus: {
        id: string;
        name: string;
        city: string;
      };
    }>;
  };
}

export interface UserProfileDetails {
  id: string;
  cnie?: string;
  passport?: string;
  nomArabe?: string;
  prenomArabe?: string;
  sexe?: string;
  dateNaissance?: string;
  paysOrigine?: string;
  handicap?: string;
  adresse?: string;
  ville?: string;
  codePostal?: string;
  phoneCountry?: string;
  urgenceNom?: string;
  urgenceNumero?: string;
  urgenceAdresse?: string;
  urgenceRelation?: string;
  urgencePhoneCountry?: string;
  // Staff fields
  rib?: string;
  salaireMensuelNet?: string;
  fonction?: string;
  diplomeId?: string;
  dateRecrutement?: string;
  // Professor fields (E-DEP)
  statutProfesseur?: string;
  departementAttacheId?: string;
  estOriginaireEtablissementPublic?: string;
  etablissementOrigine?: string;
  gradeId?: string;
  specialite?: string;
  matieresEnseignees?: string;
  tauxHoraire?: string;
  // Parent fields
  relationAvecEtudiant?: string;
  relationPrecision?: string;
  // Meta
  isComplete?: boolean;
  isLocked?: boolean;
  documents?: Array<{
    id: string;
    documentType: string;
    fileName: string;
    fileUrl: string;
    fileSize?: number;
    mimeType?: string;
    isVerified: boolean;
    verifiedBy?: string;
    verifiedAt?: string;
  }>;
}

export interface StudentDataDetails {
  id: string;
  studentCode: string;
  firstName: string;
  lastName: string;
  firstNameArabic?: string;
  lastNameArabic?: string;
  dateOfBirth?: string;
  placeOfBirth?: string;
  gender?: string;
  nationality?: string;
  maritalStatus?: string;
  cnie?: string;
  passportNumber?: string;
  email: string;
  phone: string;
  address?: string;
  city?: string;
  postalCode?: string;
  currentSemester?: number;
  enrollmentDate?: string;
  status?: string;
  // E-DEP fields
  numeroMassarCNE?: string;
  serieBaccalaureat?: string;
  anneeObtentionBac?: string;
  diplomeInscrit?: string;
  filiereInscrit?: string;
  codeFiliereInscrit?: string;
  niveauFormation?: number;
  situation?: string;
  diplomeAcces?: string;
  specialiteDiplomeAcces?: string;
  mentionDiplomeAcces?: string;
  lieuObtentionDiplomeAcces?: string;
  resident?: string;
  fonctionnaire?: string;
  pourcentageBourse?: number;
  mobilite?: string;
  premiereAnneeInscription?: string;
  // Relations
  campus?: {
    id: string;
    name: string;
    city: string;
  };
  program?: {
    id: string;
    name: string;
    code: string;
  };
  emergencyContactName?: string;
  emergencyContactPhone?: string;
  emergencyContactRelation?: string;
}

export interface TeacherDataDetails {
  id: string;
  userId: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  department?: {
    id: string;
    name: string;
    code: string;
  };
}

export interface NomenclatureData {
  grade?: { id: string; labelFr: string } | null;
  department?: { id: string; labelFr: string; code?: string } | null;
  diplome?: { id: string; labelFr: string } | null;
}

export interface ValidationRequestDetails extends PendingValidation {
  userProfile?: UserProfileDetails;
  studentData?: StudentDataDetails;
  teacherData?: TeacherDataDetails;
  nomenclatureData?: NomenclatureData;
}

export interface ReviewValidationRequest {
  decision: 'APPROVED' | 'REJECTED';
  rejectionReason?: string;
  rejectionFields?: string[];
}

export interface InstitutionalDocumentVersion {
  id: string;
  documentType: string;
  label: string;
  version: {
    id: string;
    versionNumber: number;
    fileName: string;
    originalFileName: string;
    fileUrl: string;
    fileSize: number;
    mimeType: string;
    uploadedAt: string;
  };
}

// Helper to get auth token (for URL building only)
const getAuthToken = (): string | null => {
  return localStorage.getItem('nexedu_access_token');
};

// Submit profile for validation
export const submitProfile = async (data: SubmitProfileRequest) => {
  return apiPost<unknown>('/profile-validation/submit', data);
};

export interface DossierCompleteness {
  isComplete: boolean;
  missing: { field: string; label: string }[];
}

/** What still blocks the current user's submission, as the server will judge it. */
export const getMyCompleteness = async (): Promise<DossierCompleteness> => {
  return apiGet<DossierCompleteness>('/profile-validation/completeness');
};

/** Same wording as the server refusal, with the passport rule spelled out when it applies. */
export const formatMissingDossierMessage = (missing: { label: string }[]): string => {
  const labels = missing.map((m) => m.label);
  const passportRule = labels.some((l) => /passeport/i.test(l) && /non-marocain/i.test(l))
    ? " Vous avez indiqué une nationalité autre que marocaine : le numéro de passeport et le scan du passeport sont obligatoires (la CIN peut être ajoutée en plus)."
    : '';
  return `Votre dossier est incomplet. Éléments manquants : ${labels.join(' · ')}. Renseignez-les avant de soumettre votre profil pour validation.${passportRule}`;
};

// Get validation status for current user
export const getValidationStatus = async (): Promise<ValidationStatus> => {
  return apiGet<ValidationStatus>('/profile-validation/status');
};

// Get pending validations (for managers)
export const getPendingValidations = async (campusIds?: string[]): Promise<PendingValidation[]> => {
  const params = new URLSearchParams();
  if (campusIds?.length) {
    params.append('campusIds', campusIds.join(','));
  }

  const queryString = params.toString();
  const endpoint = queryString ? `/profile-validation/pending?${queryString}` : '/profile-validation/pending';

  return apiGet<PendingValidation[]>(endpoint);
};

// Get count of pending validations (for managers)
export const getPendingValidationCount = async (campusIds?: string[]): Promise<number> => {
  const params = new URLSearchParams();
  if (campusIds?.length) {
    params.append('campusIds', campusIds.join(','));
  }

  const queryString = params.toString();
  const endpoint = queryString ? `/profile-validation/pending/count?${queryString}` : '/profile-validation/pending/count';

  try {
    const data = await apiGet<{ count: number }>(endpoint);
    return data.count;
  } catch {
    return 0;
  }
};

// Get a specific validation request with full details
export const getValidationRequest = async (requestId: string): Promise<ValidationRequestDetails> => {
  return apiGet<ValidationRequestDetails>(`/profile-validation/${requestId}`);
};

// Review (approve/reject) a validation request
export const reviewValidation = async (requestId: string, data: ReviewValidationRequest) => {
  return apiPost<unknown>(`/profile-validation/${requestId}/review`, data);
};

// Get user documents for admin review
export const getAdminUserDocuments = async (userId: string): Promise<Array<{
  id: string;
  documentType: string;
  fileName: string;
  fileUrl: string;
  fileSize?: number;
  mimeType?: string;
  isVerified: boolean;
  verifiedBy?: string;
  verifiedAt?: string;
}>> => {
  try {
    return await apiGet<Array<{
      id: string;
      documentType: string;
      fileName: string;
      fileUrl: string;
      fileSize?: number;
      mimeType?: string;
      isVerified: boolean;
      verifiedBy?: string;
      verifiedAt?: string;
    }>>(`/profile/admin/users/${userId}/documents`);
  } catch {
    console.error('Error fetching user documents');
    return [];
  }
};

// Get current version of institutional document for signing
export const getInstitutionalDocument = async (documentType: string): Promise<InstitutionalDocumentVersion | null> => {
  try {
    return await apiGet<InstitutionalDocumentVersion>(`/institutional-documents/${documentType}/current`);
  } catch {
    // Return null if document not found or error
    return null;
  }
};

// Get preview URL for institutional document
// Pass versionId to bust browser/iframe cache when a new version is uploaded
export const getInstitutionalDocumentPreviewUrl = (documentType: string, versionId?: string): string => {
  const token = getAuthToken();
  const params = new URLSearchParams();
  if (token) params.set('token', token);
  if (versionId) params.set('v', versionId);
  const query = params.toString();
  return `${API_BASE}/institutional-documents/${documentType}/current/preview${query ? `?${query}` : ''}`;
};

// Get download URL for institutional document
export const getInstitutionalDocumentDownloadUrl = (documentType: string, versionId?: string): string => {
  const token = getAuthToken();
  const params = new URLSearchParams();
  if (token) params.set('token', token);
  if (versionId) params.set('v', versionId);
  const query = params.toString();
  return `${API_BASE}/institutional-documents/${documentType}/current/download${query ? `?${query}` : ''}`;
};

// Clear a specific field during validation
export const clearProfileField = async (requestId: string, fieldName: string) => {
  return apiPost<unknown>(`/profile-validation/${requestId}/clear-field`, { fieldName });
};

// Delete a document during validation
export const deleteValidationDocument = async (requestId: string, documentId: string) => {
  return apiDelete<unknown>(`/profile-validation/${requestId}/documents/${documentId}`);
};

// Get validation request ID by user ID
export const getValidationRequestByUserId = async (userId: string): Promise<string | null> => {
  try {
    const data = await apiGet<{ id: string }>(`/profile-validation/by-user/${userId}`);
    return data.id || null;
  } catch {
    return null;
  }
};

// Update a specific field during validation (for modifications before approval)
export const updateProfileField = async (requestId: string, fieldName: string, value: string | number | boolean | null) => {
  return apiPost<unknown>(`/profile-validation/${requestId}/update-field`, { fieldName, value });
};
